import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { Add, Coffee, DeleteOutline, EventAvailable, EventBusy, ExpandMore } from '@mui/icons-material';
import { nanoid } from 'nanoid';
import {
  generateScheduleBitString,
  generateAllOnesBitString,
  getRecurringBreakAvailabilityError,
  isValidScheduleTimeRange,
  roundToNearestInterval,
  formatDateToISO,
} from '../../../time/timeUtils';
import { BitStringData, RecurringBreak } from '../../../types';
import { TIME_INTERVAL, DAYJS_DAYS_OF_WEEK } from '../../../constants';
import { dayjs, Dayjs } from '../../../utils/dayjs-config';

type DayKey = typeof DAYJS_DAYS_OF_WEEK[number];

interface ScheduleCardProps {
  day: DayKey;
  initialWorkHours: BitStringData;
  onScheduleChange: (day: DayKey, updatedSchedule: BitStringData) => void;
  recurringBreaks: RecurringBreak[];
  onRecurringBreaksChange: (day: DayKey, breaks: RecurringBreak[]) => void;
  templateEndDate: Dayjs;
}

const toDayjsFromHHmm = (time: string): Dayjs => {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = parseInt(time.slice(2), 10);
  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  day,
  initialWorkHours,
  onScheduleChange,
  recurringBreaks,
  onRecurringBreaksChange,
  templateEndDate,
}) => {
  const weekdayIndex = Math.max(0, DAYJS_DAYS_OF_WEEK.indexOf(day));
  const [workHours, setWorkHours] = useState<BitStringData>(initialWorkHours);
  const [localBreaks, setLocalBreaks] = useState<RecurringBreak[]>(recurringBreaks);
  const [timeRangeError, setTimeRangeError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setWorkHours(initialWorkHours);
    setTimeRangeError(null);
  }, [initialWorkHours]);

  useEffect(() => {
    setLocalBreaks(recurringBreaks);
  }, [recurringBreaks]);

  const hasBreak = localBreaks.length > 0;
  const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
  const recurringBreakError = useMemo(() => {
    for (const definition of localBreaks) {
      const error = getRecurringBreakAvailabilityError(workHours.bitString, definition, day);
      if (error) return error;
    }
    return null;
  }, [day, localBreaks, workHours.bitString]);

  const scheduleSummary = useMemo(() => {
    if (workHours.isDayOff) return 'Not available for bookings';
    const hours = `${workHours.startTime.format('h:mm a')} - ${workHours.endTime.format('h:mm a')}`;
    if (!hasBreak) return hours;
    return `${hours}, ${localBreaks.length} break${localBreaks.length === 1 ? '' : 's'}`;
  }, [hasBreak, localBreaks, workHours.endTime, workHours.isDayOff, workHours.startTime]);

  useEffect(() => {
    if (timeRangeError || recurringBreakError) setExpanded(true);
  }, [recurringBreakError, timeRangeError]);

  const calculateMiddleOfShift = (start: Dayjs, end: Dayjs): Dayjs => {
    const middleTimestamp = start.valueOf() + (end.valueOf() - start.valueOf()) / 2;
    return roundToNearestInterval(dayjs(middleTimestamp));
  };

  const emitScheduleChange = (updated: BitStringData) => {
    setWorkHours(updated);
    onScheduleChange(day, updated);
  };

  const emitBreaksChange = (next: RecurringBreak[]) => {
    const normalized = next.map((definition) => ({
      ...definition,
      weekday: weekdayIndex,
    }));
    setLocalBreaks(normalized);
    onRecurringBreaksChange(day, normalized);
  };

  const createDefaultBreak = (): RecurringBreak => {
    const middleTime = calculateMiddleOfShift(workHours.startTime, workHours.endTime);
    return {
      id: nanoid(),
      weekday: weekdayIndex,
      startTime: middleTime.subtract(30, 'minute').format('HHmm'),
      endTime: middleTime.add(30, 'minute').format('HHmm'),
      repeatEveryXWeeks: 1,
      repeatUntilDate: formatDateToISO(templateEndDate),
      indefinite: false,
    };
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', newTime: Dayjs) => {
    const updated = { ...workHours, [field]: newTime };
    if (!isValidScheduleTimeRange(updated.startTime, updated.endTime)) {
      setTimeRangeError('End time must be after start time. Overnight hours are not supported.');
      return;
    }

    setTimeRangeError(null);
    if (!updated.isDayOff) updated.bitString = generateScheduleBitString(updated);
    emitScheduleChange(updated);
  };

  const handleAvailabilityToggle = (available: boolean) => {
    const updated: BitStringData = available
      ? {
        ...workHours,
        isDayOff: false,
        bitString: generateScheduleBitString({ ...workHours, isDayOff: false }),
      }
      : {
        ...workHours,
        isDayOff: true,
        bitString: generateAllOnesBitString(),
      };
    emitScheduleChange(updated);
    if (!available) emitBreaksChange([]);
    setExpanded(available);
  };

  const handleBreakTimeChange = (
    breakId: string,
    field: 'startTime' | 'endTime',
    value: Dayjs | null
  ) => {
    if (!value) return;
    emitBreaksChange(localBreaks.map((definition) =>
      definition.id === breakId
        ? { ...definition, [field]: value.format('HHmm') }
        : definition
    ));
  };

  const handleRepeatIntervalChange = (breakId: string, value: number) => {
    const normalized = Math.min(4, Math.max(1, value));
    emitBreaksChange(localBreaks.map((definition) =>
      definition.id === breakId
        ? { ...definition, repeatEveryXWeeks: normalized }
        : definition
    ));
  };

  const handleRemoveBreak = (breakId: string) => {
    emitBreaksChange(localBreaks.filter((definition) => definition.id !== breakId));
  };

  const handleAddBreak = () => {
    if (!hasBreak) emitBreaksChange([createDefaultBreak()]);
  };

  const pickerSlotProps = {
    textField: {
      fullWidth: true,
      size: 'small' as const,
    },
  };

  return (
    <Card
      component="section"
      aria-labelledby={`schedule-${day}`}
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: workHours.isDayOff ? 'divider' : 'success.light',
        bgcolor: workHours.isDayOff ? 'background.paper' : 'rgba(46, 125, 50, 0.025)',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        transition: 'border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
        '&:hover': { boxShadow: '0 8px 22px rgba(16, 24, 40, 0.07)' },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: workHours.isDayOff ? 'text.secondary' : 'success.dark',
              bgcolor: workHours.isDayOff ? 'action.hover' : 'rgba(46, 125, 50, 0.12)',
            }}
          >
            {workHours.isDayOff ? <EventBusy /> : <EventAvailable />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography id={`schedule-${day}`} variant="subtitle1" sx={{ fontWeight: 800 }}>
                {dayLabel}
              </Typography>
              <Chip
                size="small"
                label={workHours.isDayOff ? 'Closed' : 'Open'}
                color={workHours.isDayOff ? 'default' : 'success'}
                variant={workHours.isDayOff ? 'outlined' : 'filled'}
                sx={{ height: 22, fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap={!workHours.isDayOff}>
              {scheduleSummary}
            </Typography>
          </Box>
          {!workHours.isDayOff && (
            <Button
              type="button"
              size="small"
              onClick={() => setExpanded((current) => !current)}
              endIcon={(
                <ExpandMore
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 180ms ease',
                  }}
                />
              )}
              aria-expanded={expanded}
              aria-controls={`schedule-details-${day}`}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, textTransform: 'none', fontWeight: 700 }}
            >
              {expanded ? 'Done' : 'Edit'}
            </Button>
          )}
          <Switch
            checked={!workHours.isDayOff}
            onChange={(event) => handleAvailabilityToggle(event.target.checked)}
            color="success"
            inputProps={{ 'aria-label': `${dayLabel} booking availability` }}
          />
        </Stack>

        {!workHours.isDayOff && (
          <Button
            type="button"
            size="small"
            fullWidth
            onClick={() => setExpanded((current) => !current)}
            endIcon={(
              <ExpandMore
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 180ms ease',
                }}
              />
            )}
            aria-expanded={expanded}
            aria-controls={`schedule-details-${day}`}
            sx={{ display: { xs: 'flex', sm: 'none' }, mt: 1, textTransform: 'none', fontWeight: 700 }}
          >
            {expanded ? 'Done editing' : `Edit ${dayLabel} hours`}
          </Button>
        )}

        <Collapse in={!workHours.isDayOff && expanded} timeout={220}>
          <Box id={`schedule-details-${day}`}>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            <TimePicker
              label="Starts"
              value={workHours.startTime}
              onChange={(newTime) => newTime && handleTimeChange('startTime', newTime)}
              minutesStep={TIME_INTERVAL}
              slotProps={pickerSlotProps}
            />
            <TimePicker
              label="Ends"
              value={workHours.endTime}
              onChange={(newTime) => newTime && handleTimeChange('endTime', newTime)}
              minutesStep={TIME_INTERVAL}
              slotProps={pickerSlotProps}
            />
          </Box>

          {timeRangeError && <Alert severity="error" sx={{ mt: 1.5 }}>{timeRangeError}</Alert>}
          {recurringBreakError && <Alert severity="error" sx={{ mt: 1.5 }}>{recurringBreakError}</Alert>}

          <Box sx={{ mt: 2 }}>
            {!hasBreak ? (
              <Button
                type="button"
                size="small"
                variant="text"
                startIcon={<Add />}
                onClick={handleAddBreak}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Add a break
              </Button>
            ) : (
              <Stack spacing={1.5}>
                {localBreaks.map((definition) => (
                  <Box
                    key={definition.id}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Coffee fontSize="small" color="action" />
                        <Typography variant="subtitle2">Break</Typography>
                      </Stack>
                      <Tooltip title="Remove break">
                        <IconButton
                          type="button"
                          size="small"
                          color="error"
                          onClick={() => handleRemoveBreak(definition.id)}
                          aria-label={`Remove ${dayLabel} break`}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                      }}
                    >
                      <TimePicker
                        label="Break starts"
                        value={toDayjsFromHHmm(definition.startTime)}
                        onChange={(newTime) => handleBreakTimeChange(definition.id, 'startTime', newTime)}
                        minutesStep={TIME_INTERVAL}
                        slotProps={pickerSlotProps}
                      />
                      <TimePicker
                        label="Break ends"
                        value={toDayjsFromHHmm(definition.endTime)}
                        onChange={(newTime) => handleBreakTimeChange(definition.id, 'endTime', newTime)}
                        minutesStep={TIME_INTERVAL}
                        slotProps={pickerSlotProps}
                      />
                    </Box>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Repeats"
                      value={definition.repeatEveryXWeeks}
                      onChange={(event) => handleRepeatIntervalChange(definition.id, Number(event.target.value))}
                      sx={{ mt: 1.5 }}
                    >
                      {[1, 2, 3, 4].map((interval) => (
                        <MenuItem key={interval} value={interval}>
                          {interval === 1 ? 'Every week' : `Every ${interval} weeks`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ScheduleCard;
